import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT_TOPICS, submitContactMessage } from "@/lib/contact.functions";
import { CaptchaField } from "@/components/captcha-field";
import { useT } from "@/i18n";

export function ContactForm({ defaultTopic }: { defaultTopic?: string }) {
  const { t } = useT();
  const send = useServerFn(submitContactMessage);
  const [topic, setTopic] = useState(defaultTopic ?? "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!captchaValid) {
      toast.error(t("shell.contact.captchaError"));
      setCaptchaInput("");
      setCaptchaKey((k) => k + 1);
      return;
    }
    setBusy(true);
    try {
      await send({ data: { topic, name, email, company, message } });
      setDone(true);
      toast.success(t("shell.contact.success"));
      setMessage("");
      setCaptchaInput("");
      setCaptchaKey((k) => k + 1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("shell.contact.error"));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="card-surface p-6 text-center">
        <h3 className="font-display text-xl font-bold">{t("shell.contact.receivedTitle")}</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("shell.contact.receivedBody", { name: name || t("shell.contact.thereFallback") })}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => setDone(false)}>
          {t("shell.contact.sendAnother")}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="card-surface grid gap-4 p-6">
      <div className="grid gap-2">
        <Label htmlFor="contact-topic">{t("shell.contact.topicLabel")}</Label>
        <Select value={topic} onValueChange={setTopic} required>
          <SelectTrigger id="contact-topic">
            <SelectValue placeholder={t("shell.contact.topicPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {CONTACT_TOPICS.map((topicOption, i) => (
              <SelectItem key={topicOption} value={topicOption}>
                {t(`shell.contact.topic.${i}` as never)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="contact-name">{t("shell.contact.nameLabel")}</Label>
          <Input
            id="contact-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="contact-email">{t("shell.contact.emailLabel")}</Label>
          <Input
            id="contact-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="contact-company">{t("shell.contact.companyLabel")}</Label>
        <Input
          id="contact-company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          autoComplete="organization"
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="contact-message">{t("shell.contact.messageLabel")}</Label>
        <Textarea
          id="contact-message"
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          placeholder={t("shell.contact.messagePlaceholder")}
        />
      </div>

      <CaptchaField
        key={captchaKey}
        id="contact-captcha"
        value={captchaInput}
        onChange={setCaptchaInput}
        onValidChange={setCaptchaValid}
      />

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" variant="hot" disabled={busy || !topic || !captchaValid}>
          {busy ? t("shell.contact.sending") : t("shell.contact.send")}
        </Button>
        <p className="text-xs text-muted-foreground">
          {t("shell.contact.privacyNote")}
        </p>
      </div>
    </form>
  );
}
